
  import { defineComponent, PropType } from 'vue'
  import { PolicyAgreement, PolicyAgreementFigure } from '@/api/TodayDeliveryApi'
  import { POLICY_SECTION } from '@/constants/todayDeliveryMessages'

  type AgreementFlowCell = { label: string; text: string; arrow: boolean }
  type AgreementExampleRow = { time: string; text: string; emphasis: boolean }
  /**
   * 예시 패널 — `rows` 는 서버 그대로(자리표시자 `{cutoff}`·`{before}`·`{after}` 포함). 고른 마감시각으로 바꾸는 일은
   * `agreementSections` 가 한다. `defaultCutoff`·`lagMinutes` 가 null 이면 옛 서버의 정적 행이라 글자 그대로 그린다.
   */
  type AgreementExample = { title: string; rows: AgreementExampleRow[]; defaultCutoff: string | null; lagMinutes: number | null }
  type AgreementFigure = { flow: AgreementFlowCell[]; example: AgreementExample }
  type AgreementGroup = {
    kind: string
    title: string
    required: boolean
    items: string[]
    /** 제목 옆 '중요' 칩 */
    important: boolean
    figure: AgreementFigure | null
  }
  type AgreementSegment = { kind: 'label' | 'em' | 'mark' | 'plain'; text: string }
  type MarkedSegment = { kind: 'mark' | 'plain'; text: string }

  const trimmedText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

  /** 예시 행 자리표시자 — 이 셋 말고 다른 `{…}` 가 있으면 그림을 버린다 */
  const EXAMPLE_PLACEHOLDERS = ['cutoff', 'before', 'after']
  const PLACEHOLDER = /\{([^{}]*)\}/g
  const placeholderKeys = (text: string): string[] => [...text.matchAll(PLACEHOLDER)].map((match) => match[1])
  const DAY_MINUTES = 24 * 60

  /** 'HH:MM'(seconds 가 true 면 'HH:MM:SS' 도)을 자정부터 분으로. 시 00~23·분 00~59 가 아니면 null. */
  const cutoffMinutes = (value: unknown, seconds: boolean): number | null => {
    const matched = (seconds ? /^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/ : /^([01]\d|2[0-3]):([0-5]\d)$/).exec(trimmedText(value))
    return matched ? Number(matched[1]) * 60 + Number(matched[2]) : null
  }
  const cutoffMinutesText = (value: unknown, seconds: boolean): string | null => {
    const minutes = cutoffMinutes(value, seconds)
    return minutes === null ? null : clockText(minutes)
  }
  /** 분을 'HH:MM' 으로 — 24시간을 넘거나 0 아래로 내려가면 하루 단위로 감는다 */
  const clockText = (minutes: number): string => {
    const wrapped = ((minutes % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES
    return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`
  }

  /**
   * 카드 안 그림(`figure`)을 그릴 수 있는 모양일 때만 넘긴다 — 아니면 null 이고 카드는 문장만 그린다.
   * 그림은 문장을 돕는 화면 구성이라 모양이 어긋나도 **그림만** 버린다(카드·동의 판정은 그대로).
   * 흐름 띠 3칸 = [칸, 화살표, 칸] — 가운데만 `arrow`, 양 끝은 라벨·본문, 가운데는 라벨(강조 글자)이 있어야 한다.
   * 예시 패널 = 제목 + 행 1~4개, 행마다 시각·설명이 있어야 한다. 옛 모양(글자 배열)도 여기서 null 이 된다.
   * 행 자리표시자는 `{cutoff}`·`{before}`·`{after}` 셋뿐이다 — 다른 `{…}` 가 있으면 null. 자리표시자를 쓰면 `default_cutoff`(HH:MM)와
   * `lag_minutes`(1~60 정수)가 있어야 한다. 두 칸은 오면 늘 검사한다(틀리면 null). 둘 다 없고 자리표시자도 없으면 옛 정적 행이다.
   */
  const agreementFigure = (raw: PolicyAgreementFigure | null | undefined): AgreementFigure | null => {
    if (!raw || typeof raw !== 'object' || raw.type !== 'cutoff_timeline') return null
    if (!Array.isArray(raw.flow) || raw.flow.length !== 3) return null
    const flow = raw.flow.map((cell) => ({ label: trimmedText(cell?.label), text: trimmedText(cell?.text), arrow: cell?.arrow === true }))
    const [start, arrow, finish] = flow
    if (start.arrow || !arrow.arrow || finish.arrow) return null
    if (!start.label || !start.text || !arrow.label || !finish.label || !finish.text) return null
    const example = raw.example
    if (!example || typeof example !== 'object' || Array.isArray(example)) return null
    const title = trimmedText(example.title)
    if (!title || !Array.isArray(example.rows) || example.rows.length < 1 || example.rows.length > 4) return null
    const rows = example.rows.map((row) => ({
      time: trimmedText(row?.time),
      text: trimmedText(row?.text),
      emphasis: row?.emphasis === true,
    }))
    if (rows.some((row) => !row.time || !row.text)) return null
    const keys = rows.flatMap((row) => [...placeholderKeys(row.time), ...placeholderKeys(row.text)])
    if (keys.some((key) => !EXAMPLE_PLACEHOLDERS.includes(key))) return null
    const hasDefault = example.default_cutoff !== undefined && example.default_cutoff !== null
    const defaultCutoff = hasDefault ? cutoffMinutesText(example.default_cutoff, false) : null
    if (hasDefault && !defaultCutoff) return null
    const hasLag = example.lag_minutes !== undefined && example.lag_minutes !== null
    const lagMinutes = hasLag && Number.isInteger(example.lag_minutes) ? Number(example.lag_minutes) : null
    if (hasLag && (lagMinutes === null || lagMinutes < 1 || lagMinutes > 60)) return null
    if (keys.length && (!defaultCutoff || lagMinutes === null)) return null
    return { flow, example: { title, rows, defaultCutoff, lagMinutes } }
  }

  /**
   * 예시 행의 자리표시자를 고른 마감시각으로 바꾼다 — `{cutoff}` 고른 시각, `{before}`·`{after}` 그보다 `lagMinutes` 분 앞·뒤.
   * 고른 시각이 없거나 형식이 틀리면 서버 `default_cutoff`. 옛 정적 행(`defaultCutoff`·`lagMinutes` 없음)은 그대로 둔다.
   * `**…**` 강조 파싱보다 **먼저** 바꾼다(`**{cutoff}까지**` → `**14:00까지**`) — 결과는 화면이 글자로만 그린다.
   */
  const resolveExampleRows = (example: AgreementExample, selectedCutoff: string): AgreementExampleRow[] => {
    if (!example.defaultCutoff || example.lagMinutes === null) return example.rows
    const base = cutoffMinutes(selectedCutoff, true) ?? (cutoffMinutes(example.defaultCutoff, false) as number)
    const values: Record<string, string> = {
      cutoff: clockText(base),
      before: clockText(base - example.lagMinutes),
      after: clockText(base + example.lagMinutes),
    }
    const fill = (text: string) => text.replace(PLACEHOLDER, (token, key: string) => values[key] ?? token)
    return example.rows.map((row) => ({ ...row, time: fill(row.time), text: fill(row.text) }))
  }

  /**
   * 서버 강조 표기 `**…**` 를 조각으로 — 짝이 맞는 구간만 'mark', 나머지는 글자 그대로(짝 없는 `**` 도 글자로 남는다).
   * 조각은 텍스트로만 그린다(v-html 없음) — 조각을 이으면 짝 맞는 `**` 만 빠진 원문이다. 표기가 없으면 원문 한 조각.
   */
  const MARK = '**'
  const markedSegments = (text: string): MarkedSegment[] => {
    const parts = String(text ?? '').split(MARK)
    const segments: MarkedSegment[] = []
    const push = (kind: MarkedSegment['kind'], value: string) => {
      if (!value) return
      const last = segments[segments.length - 1]
      if (kind === 'plain' && last?.kind === 'plain') last.text += value
      else segments.push({ kind, text: value })
    }
    parts.forEach((part, index) => {
      if (index % 2 === 0) push('plain', part)
      else if (index === parts.length - 1) push('plain', MARK + part) // 닫는 표기가 없다 — 여는 `**` 를 글자로 둔다
      else push(part ? 'mark' : 'plain', part || MARK + MARK) // 빈 구간 `****` 는 글자 그대로
    })
    return segments
  }

  // 동의 문안 강조 — 글자를 바꾸지 않고 **조각으로 나눠 모양만** 입힌다(조각을 이으면 원문 그대로).
  // 머리말 라벨: 문장 맨 앞 `배송 지연 페널티:`·`품절 취소:` 처럼 짧은 이름 뒤 쌍점까지(쉼표·마침표가 끼면 라벨이 아니다).
  const AGREEMENT_LABEL = /^([^:：.,]{2,16}[:：]\s?)/
  // 금액·기한: `최대 2,000P` · `1,000P` · `3영업일` · `오전 11시` · `24시간` · `30일 이내` · `최대 3장`
  const AGREEMENT_EMPHASIS = /(최대 [\d,]+P|[\d,]+P|\d+영업일|오전 \d+시|\d+시간|\d+일 이내|최대 \d+장)/g

  /**
   * 카드 문장 한 줄의 조각. 서버가 `**…**` 로 강조를 정한 문장은 그 표기만 따른다(자동 강조를 더하지 않는다).
   * 표기가 없는 문장은 예전 그대로 머리말 라벨·금액·기한 자동 강조다 — 렌더가 바이트까지 같다.
   */
  const agreementSegments = (text: string): AgreementSegment[] => {
    const source = String(text ?? '')
    const marked = markedSegments(source)
    if (marked.some((seg) => seg.kind === 'mark')) return marked
    const segments: AgreementSegment[] = []
    let rest = source
    const label = source.match(AGREEMENT_LABEL)
    if (label) {
      segments.push({ kind: 'label', text: label[1] })
      rest = source.slice(label[1].length)
    }
    let last = 0
    for (const match of rest.matchAll(AGREEMENT_EMPHASIS)) {
      const start = match.index ?? 0
      if (start > last) segments.push({ kind: 'plain', text: rest.slice(last, start) })
      segments.push({ kind: 'em', text: match[0] })
      last = start + match[0].length
    }
    if (last < rest.length) segments.push({ kind: 'plain', text: rest.slice(last) })
    return segments
  }

  /**
   * 오늘출발 정책 동의 본문 (배송정책 관리 동의 모달에서 뽑음, PM 2026-10-13).
   *
   * 카드 확인 상태는 이 컴포넌트가 들고, 동의가 성립했는지(문안을 받았고 필수 카드를 모두 확인)만 `update:complete` 로 알린다.
   * 문안이 없거나 비면 동의가 성립하지 않는다(fail-closed) — 빈 본문에 동의시키지 않는다.
   */
  export default defineComponent({
    name: 'TodayDeliveryAgreementPanel',

    props: {
      /** 서버 동의 문안(`policy_agreement`) — 그리기만 한다 */
      agreement: { type: Object as PropType<PolicyAgreement | null>, default: null },
      /** 셀러가 고른 주문 마감시각('HH:MM') — 예시 행 자리표시자를 이 값으로 바꾼다. 비었거나 틀리면 서버 `default_cutoff` */
      cutoff: { type: String, default: '' },
    },

    emits: ['update:complete'],

    data() {
      return {
        /** groups 메타가 없는 구 응답(평문)일 때만 쓰는 한 줄 동의 */
        agreementChecked: false,
        /** groups 메타가 있을 때 카드별 확인 — `agreementGroups` 와 같은 순서·길이 */
        groupChecks: [] as boolean[],
        msg: POLICY_SECTION,
      }
    },

    computed: {
      /**
       * 동의 문안은 서버 단일 원천(`policy_agreement`)을 그대로 그린다 — 화면에 사본을 두지 않는다.
       * **공백만 있는 항목은 버린다** — 이 정규화 배열을 렌더링과 검증에 **함께** 쓴다.
       * 둘이 다르면 "보이는 것" 과 "검사하는 것" 이 갈려 빈 약관에 동의가 성립한다(r3 R-SADM-6).
       */
      agreementItems(): string[] {
        return (this.agreement?.items || []).map((item) => String(item ?? '').trim()).filter(Boolean)
      },
      /**
       * groups 메타가 모든 보이는 항목을 중복 없이 덮을 때만 카드를 쓴다.
       * 구 응답·불완전 메타는 전체 items 평문으로 폴백해 "보이는 것 = 검사하는 것"을 유지한다.
       * 카드 꾸밈 칸(`important`·`figure`)은 이 폴백 판단에 끼지 않는다 — 그림 모양이 어긋나면 그 카드의 그림만 null 이 된다.
       */
      agreementGroups(): AgreementGroup[] {
        const agreement = this.agreement
        const groups = agreement?.groups
        if (!Array.isArray(groups) || !groups.length) return []

        const rawItems = (agreement?.items || []).map((item) => String(item ?? '').trim())
        const expectedIndexes = rawItems.map((item, index) => (item ? index : -1)).filter((index) => index >= 0)
        const usedIndexes: number[] = []
        const normalized = groups.map((group) => {
          if (!group?.title || (group.kind !== 'OPERATION' && group.kind !== 'PENALTY')) return null
          if (!Array.isArray(group.item_indexes) || !group.item_indexes.length) return null
          const indexes = group.item_indexes
          if (indexes.some((index) => !Number.isInteger(index) || index < 0 || !rawItems[index])) return null
          usedIndexes.push(...indexes)
          return {
            kind: group.kind,
            title: group.title,
            required: group.required === true,
            items: indexes.map((index) => rawItems[index]),
            important: group.important === true,
            figure: agreementFigure(group.figure),
          }
        })

        if (normalized.some((group) => !group)) return []
        const sortedUsed = [...usedIndexes].sort((a, b) => a - b)
        if (new Set(usedIndexes).size !== usedIndexes.length || JSON.stringify(sortedUsed) !== JSON.stringify(expectedIndexes)) return []
        return normalized as AgreementGroup[]
      },
      /** 카드를 신청 페이지처럼 '운영 기준' · '페널티' 두 구역으로 묶는다(구역 안 순서는 서버 순서 그대로) */
      /**
       * 카드 묶음. 카드마다 `figure` 는 예시 행 자리표시자를 셀러가 고른 마감시각(`cutoff`)으로 바꾼 그림이다 —
       * computed 라 고른 값이 바뀌면 다시 계산된다.
       */
      agreementSections(): {
        kind: string
        title: string
        groups: { index: number; group: AgreementGroup; figure: AgreementFigure | null }[]
      }[] {
        const selectedCutoff = this.cutoff
        const entries = (this.agreementGroups as AgreementGroup[]).map((group, index) => ({
          index,
          group,
          figure: group.figure
            ? { ...group.figure, example: { ...group.figure.example, rows: resolveExampleRows(group.figure.example, selectedCutoff) } }
            : null,
        }))
        return [
          { kind: 'OPERATION', title: POLICY_SECTION.agreementSectionOperation },
          { kind: 'PENALTY', title: POLICY_SECTION.agreementSectionPenalty },
        ]
          .map((section) => ({ ...section, groups: entries.filter((entry) => entry.group.kind === section.kind) }))
          .filter((section) => section.groups.length > 0)
      },
      /** 필수 카드(없으면 전부) — 동의 버튼은 이것이 모두 확인돼야 열린다 */
      requiredGroupIndexes(): number[] {
        const groups = this.agreementGroups as AgreementGroup[]
        const required = groups.map((group, index) => (group.required ? index : -1)).filter((index) => index >= 0)
        return required.length ? required : groups.map((_, index) => index)
      },
      allAgreementChecked: {
        get(): boolean {
          const groups = this.agreementGroups as AgreementGroup[]
          return groups.length > 0 && groups.every((_, index) => this.groupChecks[index] === true)
        },
        set(value: boolean) {
          this.groupChecks = (this.agreementGroups as AgreementGroup[]).map(() => value)
        },
      },
      /** 동의가 성립했는지 — 카드가 있으면 필수 카드 확인, 구 응답이면 한 줄 동의 */
      agreementComplete(): boolean {
        if ((this.agreementGroups as AgreementGroup[]).length) {
          return this.requiredGroupIndexes.every((index) => this.groupChecks[index] === true)
        }
        return this.agreementChecked
      },
      agreementVersion(): string {
        // 서버 문안의 버전만 인정한다 — `policy_version` 으로 폴백하면 **빈 문안에 동의**가 성립한다.
        // 공백뿐인 버전(' ')도 버전이 아니다.
        return String(this.agreement?.version ?? '').trim()
      },
      /** 동의를 받을 수 있는 상태인지 — 버전과 항목이 모두 있어야 한다(없으면 fail-closed) */
      agreementReady(): boolean {
        return !!this.agreementVersion && this.agreementItems.length > 0
      },
      /** 동의 버튼을 열 수 있는지 — 문안을 받았고(`agreementReady`) 동의가 성립했다(`agreementComplete`) */
      canAgree(): boolean {
        return this.agreementReady && this.agreementComplete
      },
    },

    watch: {
      canAgree: {
        handler(value: boolean) {
          this.$emit('update:complete', value)
        },
        immediate: true,
      },
    },

    created() {
      // 그릴 때마다 모두 해제한 채 시작한다
      this.groupChecks = (this.agreementGroups as AgreementGroup[]).map(() => false)
    },

    methods: {
      /** 동의 문안 한 항목을 강조 조각으로 — 조각을 이으면 원문과 같다 */
      agreementSegments(text: string): AgreementSegment[] {
        return agreementSegments(text)
      },
      /** 서버 강조 표기 `**…**` 만 조각으로(예시 행·평문 폴백) — 조각은 텍스트로만 그린다 */
      markedSegments(text: string): MarkedSegment[] {
        return markedSegments(text)
      },
    },
  })
